/**
 * Schlichtes Komponentenset: Tabelle, Filterleiste, Detailpanel, Dialog, Toast,
 * Kachel, Zustandsanzeigen. Keine UI-Bibliothek, damit Tastaturfokus und
 * Scanner-Eingaben unter eigener Kontrolle bleiben.
 */
import {
  createContext, useCallback, useContext, useEffect, useRef, useState, type FormEvent, type ReactNode,
} from 'react';
import { ROLLEN_RANG, type Rolle, type SessionUser } from '@storeos/contracts';

// ── Benutzer / Rollen ─────────────────────────────────────────────────────

export const BenutzerKontext = createContext<SessionUser | null>(null);
export const useBenutzer = () => useContext(BenutzerKontext)!;
export const useDarf = () => {
  const u = useContext(BenutzerKontext);
  return (min: Rolle) => !!u && ROLLEN_RANG[u.rolle] >= ROLLEN_RANG[min];
};
/** Rendert Kinder nur ab Mindestrolle (Knöpfe, die eine Rolle nicht nutzen darf, gibt es für sie nicht). */
export function Darf({ rolle, children }: { rolle: Rolle; children: ReactNode }) {
  return useDarf()(rolle) ? <>{children}</> : null;
}

// ── Toast ─────────────────────────────────────────────────────────────────

type ToastArt = 'ok' | 'fehler' | 'info';
interface ToastEintrag { id: number; text: string; art: ToastArt }
const ToastKontext = createContext<(text: string, art?: ToastArt) => void>(() => {});
export const useToast = () => useContext(ToastKontext);

export function ToastBereich({ children }: { children: ReactNode }) {
  const [liste, setListe] = useState<ToastEintrag[]>([]);
  const zeige = useCallback((text: string, art: ToastArt = 'ok') => {
    const id = Date.now() + Math.random();
    setListe((l) => [...l.slice(-3), { id, text, art }]);
    setTimeout(() => setListe((l) => l.filter((t) => t.id !== id)), art === 'fehler' ? 7000 : 4000);
  }, []);
  return (
    <ToastKontext.Provider value={zeige}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {liste.map((t) => <div key={t.id} className={`toast toast-${t.art}`}>{t.text}</div>)}
      </div>
    </ToastKontext.Provider>
  );
}

// ── Dialog ────────────────────────────────────────────────────────────────

/** Modaler Dialog (natives <dialog>: Fokusfalle, Esc schließt). */
export function Dialog({ offen, titel, onSchliessen, children, breit }: { offen: boolean; titel: string; onSchliessen: () => void; children: ReactNode; breit?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (offen && !d.open) {
      d.showModal();
      // showModal fokussiert sonst „Schließen“; das erste Eingabefeld ist für Tastaturbedienung richtig.
      d.querySelector<HTMLElement>('.dialog-inhalt input:not([type=hidden]), .dialog-inhalt select, .dialog-inhalt textarea')?.focus();
    }
    if (!offen && d.open) d.close();
  }, [offen]);
  return (
    <dialog ref={ref} className={`dialog${breit ? ' breit' : ''}`} onCancel={(e) => { e.preventDefault(); onSchliessen(); }} aria-label={titel}>
      {offen && (
        <>
          <header><h2>{titel}</h2><button type="button" className="knopf-flach" onClick={onSchliessen} aria-label="Schließen">Schließen</button></header>
          <div className="dialog-inhalt">{children}</div>
        </>
      )}
    </dialog>
  );
}

/** Formular im Dialog mit Absenden/Abbrechen und Fehlerzeile. */
export function DialogFormular({ onAbsenden, onAbbrechen, absendenText, children, gefahr, sperre }: {
  onAbsenden: () => Promise<unknown> | void; onAbbrechen: () => void; absendenText: string; children: ReactNode; gefahr?: boolean; sperre?: boolean;
}) {
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null); setLaeuft(true);
    try { await onAbsenden(); } catch (err) { setFehler((err as Error).message); } finally { setLaeuft(false); }
  }
  return (
    <form onSubmit={absenden} className="formular">
      {children}
      {fehler && <p className="fehler" role="alert">{fehler}</p>}
      <div className="knopfreihe">
        <button type="button" className="knopf-zweit" onClick={onAbbrechen}>Abbrechen</button>
        <button type="submit" className={gefahr ? 'knopf-gefahr' : 'knopf'} disabled={laeuft || sperre}>{laeuft ? '…' : absendenText}</button>
      </div>
    </form>
  );
}

// ── Tabelle ───────────────────────────────────────────────────────────────

export interface Spalte<T> {
  kopf: string;
  zelle: (z: T) => ReactNode;
  klasse?: string;
  rechts?: boolean;
}

export function Tabelle<T>({ spalten, zeilen, schluessel, onZeile, aktiv, leer = 'Keine Einträge', testid }: {
  spalten: Spalte<T>[]; zeilen: T[]; schluessel: (z: T) => string; onZeile?: (z: T) => void; aktiv?: string | null; leer?: string; testid?: string;
}) {
  return (
    <div className="tabelle-rahmen">
      <table className="tabelle" data-testid={testid}>
        <thead><tr>{spalten.map((s) => <th key={s.kopf} className={`${s.klasse ?? ''}${s.rechts ? ' rechts' : ''}`}>{s.kopf}</th>)}</tr></thead>
        <tbody>
          {zeilen.length === 0 && <tr><td colSpan={spalten.length} className="leer">{leer}</td></tr>}
          {zeilen.map((z) => {
            const k = schluessel(z);
            return (
              <tr key={k} className={`${onZeile ? 'klickbar' : ''}${aktiv === k ? ' aktiv' : ''}`} tabIndex={onZeile ? 0 : undefined}
                onClick={onZeile ? () => onZeile(z) : undefined}
                onKeyDown={onZeile ? (e) => { if (e.key === 'Enter') onZeile(z); } : undefined}>
                {spalten.map((s) => <td key={s.kopf} className={`${s.klasse ?? ''}${s.rechts ? ' rechts' : ''}`}>{s.zelle(z)}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Filterleiste, Seitenkopf, Panel, Kachel ───────────────────────────────

export function Filterleiste({ children }: { children: ReactNode }) {
  return <div className="filterleiste" role="search">{children}</div>;
}

export function Seitenkopf({ titel, children, unter }: { titel: string; children?: ReactNode; unter?: ReactNode }) {
  return (
    <div className="seitenkopf">
      <div><h1>{titel}</h1>{unter && <div className="unter">{unter}</div>}</div>
      {children && <div className="aktionen">{children}</div>}
    </div>
  );
}

export function Detailpanel({ titel, onSchliessen, children }: { titel: ReactNode; onSchliessen: () => void; children: ReactNode }) {
  return (
    <aside className="detailpanel" aria-label="Detail">
      <header><h2>{titel}</h2><button type="button" className="knopf-flach" onClick={onSchliessen}>Schließen</button></header>
      <div className="detailpanel-inhalt">{children}</div>
    </aside>
  );
}

export function Karte({ titel, children, aktion, klasse }: { titel?: ReactNode; children: ReactNode; aktion?: ReactNode; klasse?: string }) {
  return (
    <section className={`karte ${klasse ?? ''}`}>
      {(titel || aktion) && <header><h2>{titel}</h2>{aktion}</header>}
      {children}
    </section>
  );
}

export function Kachel({ wert, text, klasse, onClick }: { wert: ReactNode; text: string; klasse?: string; onClick?: () => void }) {
  const Inhalt = <><span className="kachel-wert">{wert}</span><span className="kachel-text">{text}</span></>;
  return onClick
    ? <button type="button" className={`kachel ${klasse ?? ''}`} onClick={onClick}>{Inhalt}</button>
    : <div className={`kachel ${klasse ?? ''}`}>{Inhalt}</div>;
}

export function Zustand({ laden, fehler, children }: { laden?: boolean; fehler?: unknown; children?: ReactNode }) {
  if (fehler) return <p className="fehler" role="alert" data-testid="zustand-fehler">Fehler: {(fehler as Error).message}</p>;
  if (laden) return <p className="hinweis" data-testid="zustand-laden">Lädt …</p>;
  return <>{children}</>;
}

export function Feld({ label, children, hinweis }: { label: string; children: ReactNode; hinweis?: string }) {
  return <label className="feld"><span>{label}</span>{children}{hinweis && <small className="hinweis">{hinweis}</small>}</label>;
}
